import { computed, ref, watch, unref, type Ref } from 'vue'
import { cloneDeep, isEqual } from 'lodash'
import { useLoadInventObjectType } from '@/entities/Inventory/ObjectTypes'
import { useSendForm } from '@/features/Inventory/ObjectTypes/model/api'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { normalizeGridTemplateChrome } from './normalizeGridTemplateChrome'
import { pruneSummaryLayoutOrphansForTable } from './tableLayout'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import {
  fieldTypesFromClasses,
  normalizeObjectTypeFieldsResponse,
  objectTypeFieldsToInventoryFields,
  useLoadPassportBuilderObjectTypeFields,
  type ObjectTypeFieldsResponse,
} from './objectTypeFields'
import { objectTypeFieldsToGridTemplate } from './objectTypeFieldLayout'

type ObjectTypeFormRecord = Record<string, unknown> & {
  object_type?: { id?: number; name?: string; object_name?: string }
  parent_invent_object_type?: { id?: number }
  id?: number
  styles?: { stroke?: { width?: number | string } }
  parent_invent_type_id?: number
}

function pickNonEmptyString(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
  }
  return ''
}

/** GET /object-types/{id} often returns `object_type: { id, is_system }` without `name`; header pickers need a label. */
function resolveObjectTypeDisplayNameForHeader(d: Record<string, unknown>): string {
  const ot = d.object_type
  if (ot && typeof ot === 'object') {
    const o = ot as Record<string, unknown>
    const fromOt = pickNonEmptyString(o.name, o.object_name, o.title)
    if (fromOt) return fromOt
  }
  for (const key of ['list_invent_object_type', 'parent_invent_object_type', 'invent_object_type'] as const) {
    const node = d[key]
    if (node && typeof node === 'object') {
      const n = node as Record<string, unknown>
      const label = pickNonEmptyString(n.name, n.object_name, n.title)
      if (label) return label
    }
  }
  return ''
}

/**
 * Root state for Passport Builder v2: loads base type data plus normalized fields/layout state.
 */
export function useBuilderState(
  objectTypeId: Ref<string | number>,
  objectId: Ref<string>,
) {
  const catalogTypeId = computed(() => Number(objectTypeId.value) || 0)
  const objectTypeFieldsEnabled = computed(() => catalogTypeId.value > 0)

  const objectTypeQuery = useLoadInventObjectType(objectTypeId, objectId)
  const objectTypeFieldsQuery = useLoadPassportBuilderObjectTypeFields(
    catalogTypeId,
    objectTypeFieldsEnabled,
  )
  const objectTypeFieldsResponse = computed<ObjectTypeFieldsResponse>(() =>
    normalizeObjectTypeFieldsResponse(objectTypeFieldsQuery.data.value),
  )

  const paletteFields = computed<ObjectInventoryField[]>(() => [])

  function mergedAllForPrune(): ObjectInventoryField[] {
    return fields.value
  }

  const fields = ref<ObjectInventoryField[]>([])
  const gridTemplate = ref<GridFieldType[]>([])
  const fieldGroups = ref<ObjectTypeGroup[]>([])
  const parentFields = ref<ObjectInventoryField[]>([])
  const formBase = ref<ObjectTypeFormRecord | null>(null)
  const savedFormBaseSnapshot = ref<ObjectTypeFormRecord | null>(null)
  const savedGridSnapshot = ref<GridFieldType[] | null>(null)
  const savedFieldsSnapshot = ref<ObjectInventoryField[] | null>(null)
  const savedFieldGroupsSnapshot = ref<ObjectTypeGroup[] | null>(null)

  const markSaved = () => {
    savedFormBaseSnapshot.value = cloneDeep(formBase.value)
    savedGridSnapshot.value = cloneDeep(gridTemplate.value)
    savedFieldsSnapshot.value = cloneDeep(fields.value)
    savedFieldGroupsSnapshot.value = cloneDeep(fieldGroups.value)
  }

  watch(
    () => objectTypeQuery.data.value?.data,
    (raw) => {
      if (!raw) return
      const data = raw as ObjectTypeFormRecord & { invent_object_actions?: unknown[] }
      formBase.value = cloneDeep({
        ...data,
        invent_object_actions: data.invent_object_actions ?? [],
      })
      const headerLabel = resolveObjectTypeDisplayNameForHeader(data as Record<string, unknown>)
      if (headerLabel && formBase.value?.object_type && typeof formBase.value.object_type === 'object') {
        const cur = formBase.value.object_type as Record<string, unknown>
        if (!pickNonEmptyString(cur.name)) {
          formBase.value = {
            ...formBase.value,
            object_type: { ...cur, name: headerLabel } as ObjectTypeFormRecord['object_type'],
          }
        }
      }
      markSaved()
      const parentType = data.parent_invent_object_type as
        | { id?: number; fields?: ObjectInventoryField[] | string; field_groups?: ObjectTypeGroup[] }
        | undefined
      if (parentType?.fields) {
        try {
          const pf =
            typeof parentType.fields === 'string' ? JSON.parse(parentType.fields) : parentType.fields
          parentFields.value = cloneDeep(Array.isArray(pf) ? pf : [])
        } catch {
          parentFields.value = []
        }
      } else {
        parentFields.value = []
      }
    },
    { immediate: true },
  )

  watch(
    () => objectTypeFieldsResponse.value,
    (normalized) => {
      if (!objectTypeFieldsQuery.data.value) return
      const allFieldTypes = fieldTypesFromClasses(normalized.fieldClasses)
      fields.value = cloneDeep(objectTypeFieldsToInventoryFields(normalized.fields, allFieldTypes))
      fieldGroups.value = cloneDeep(normalized.fieldGroups)

      const mergedForPrune = mergedAllForPrune()
      let nextGrid = objectTypeFieldsToGridTemplate(normalized.fields, normalized.fieldClasses)
      nextGrid = pruneSummaryLayoutOrphansForTable(nextGrid, mergedForPrune)
      if (catalogTypeId.value > 0) {
        nextGrid = normalizeGridTemplateChrome(nextGrid, mergedForPrune, catalogTypeId.value)
      }
      gridTemplate.value = nextGrid
      markSaved()
    },
    { immediate: true },
  )

  watch(
    () =>
      [
        fields.value,
        paletteFields.value,
        formBase.value,
        objectTypeId.value,
      ] as const,
    () => {
      const merged = mergedAllForPrune()
      const catalogTypeId =
        Number(unref(objectTypeId)) ||
        Number(formBase.value?.object_type?.id) ||
        Number(formBase.value?.id) ||
        0
      let pruned = pruneSummaryLayoutOrphansForTable(gridTemplate.value, merged)
      if (catalogTypeId > 0) {
        pruned = normalizeGridTemplateChrome(pruned, merged, catalogTypeId)
      }
      if (!isEqual(pruned, gridTemplate.value)) {
        gridTemplate.value = pruned
      }
    },
    { deep: true },
  )

  const isDirty = computed(() => {
    if (
      !savedFormBaseSnapshot.value ||
      !savedGridSnapshot.value ||
      !savedFieldsSnapshot.value ||
      !savedFieldGroupsSnapshot.value
    ) {
      return false
    }
    return (
      !isEqual(formBase.value, savedFormBaseSnapshot.value) ||
      !isEqual(gridTemplate.value, savedGridSnapshot.value) ||
      !isEqual(fields.value, savedFieldsSnapshot.value) ||
      !isEqual(fieldGroups.value, savedFieldGroupsSnapshot.value)
    )
  })

  const isLoading = computed(
    () =>
      objectTypeQuery.isLoading.value ||
      objectTypeQuery.isFetching.value ||
      objectTypeFieldsQuery.isLoading.value ||
      objectTypeFieldsQuery.isFetching.value,
  )
  const objectTypeFieldsLoading = computed(
    () => objectTypeFieldsQuery.isLoading.value || objectTypeFieldsQuery.isFetching.value,
  )

  const { sendForm, isPending: isSaving } = useSendForm(unref(objectId))

  /**
   * Обновляет базовый тип и новую модель полей/раскладки одним вызовом.
   */
  const refetch = async () => {
    const [objectTypeResult, objectTypeFieldsResult] = await Promise.all([
      objectTypeQuery.refetch(),
      objectTypeFieldsQuery.refetch(),
    ])

    return objectTypeFieldsResult ?? objectTypeResult
  }

  /**
   * Обновляет только нормализованную модель полей текущего типа.
   */
  const refetchObjectTypeFields = async (): Promise<ObjectTypeFieldsResponse> => {
    const result = await objectTypeFieldsQuery.refetch()
    return normalizeObjectTypeFieldsResponse(result.data)
  }

  const normalizeForSave = (data: ObjectTypeFormRecord) => {
    if (data.styles?.stroke && data.styles.stroke.width !== undefined) {
      data.styles.stroke.width = Number(data.styles.stroke.width)
    }
    data.group_id = Number(unref(objectId))
    if (data.object_type?.id != null) {
      data.object_type_id = data.object_type.id
    }
    data.parent_invent_type_id = data.parent_invent_type_id || 0

    /** Prefer current catalog `object_type.id` so own grid cells are not filled with the parent type id (reload then looked like «Справочники»). */
    const fallbackInventoryType = (() => {
      const ot = data.object_type as { id?: number } | undefined
      if (ot?.id != null && Number(ot.id) > 0) return Number(ot.id)
      return Number(data.parent_invent_object_type?.id ?? data.id) || 0
    })()
    const tpl = data.passport_grid_template as GridFieldType[] | undefined
    if (tpl?.length) {
      const canFillInventory = Boolean(fallbackInventoryType && fallbackInventoryType > 0)
      data.passport_grid_template = tpl.map((item) => {
        const rec = { ...(item as GridFieldType & Record<string, unknown>) }
        delete rec.builder_tab_ref
        if (rec.group_id == null || Number(rec.group_id) <= 0) {
          delete rec.group_id
        }
        const isField = !rec.type || rec.type === 'field'
        const inv = rec.inventory_type
        const needsInventoryType =
          canFillInventory && isField && (inv == null || Number(inv) <= 0)
        if (needsInventoryType) {
          return { ...rec, inventory_type: fallbackInventoryType }
        }
        return rec
      }) as unknown as ObjectTypeFormRecord['passport_grid_template']
    }
  }

  /**
   * Persists the current object type; `formValid` mirrors the legacy passport form (VForm validity).
   */
  const save = async (
    formValid: boolean,
    options?: { navigate?: boolean; notify?: boolean },
  ): Promise<boolean> => {
    if (!formBase.value) return false
    const data = cloneDeep(formBase.value) as ObjectTypeFormRecord
    data.fields = fields.value as unknown as ObjectInventoryField[]
    data.passport_grid_template = gridTemplate.value as unknown as ObjectTypeFormRecord['passport_grid_template']
    data.field_groups = fieldGroups.value as unknown as ObjectTypeFormRecord['field_groups']
    normalizeForSave(data)
    const ok = await sendForm(data, formValid, options)
    if (ok) {
      markSaved()
    }
    return ok
  }

  return {
    fields,
    gridTemplate,
    fieldGroups,
    parentFields,
    formBase,
    paletteFields,
    isLoading,
    objectTypeFieldsLoading,
    isSaving,
    isDirty,
    save,
    markSaved,
    refetch,
    refetchObjectTypeFields,
    objectTypeQuery,
    objectTypeFieldsQuery,
    objectTypeFieldsResponse,
  }
}
